import { useEffect, useState } from "react";
import { useAppSelector } from "@lib";
import { createPaymentAccessToken } from "@lib";
import { PAYMENT_KEYS } from "@lib/utilities/Constants";
import {
  formatPortalOneError,
  getPaymentInfo,
  getPrimaryPolicyHolderId,
} from "@lib/utilities/HelperFunctions";
import {
  toOneIncPaymentSession,
  toPaymentSession,
} from "../billing/billingPaymentParser";
import { introspectionAPI } from "@lib";
import { messages } from "../../../src/resources/strings";

const usePayment = ({
  onClose,
  closePayeeModal,
  containerRef,
  onError,
}: {
  onClose: () => void;
  closePayeeModal: () => void;
  onError: (error: any) => void;
  containerRef: any;
}) => {
  const { policyNumber, policyHolders } = useAppSelector(
    (state) => state.policyDetailReducer,
  );
  const [isLoading, setIsLoading] = useState(false);

  const [session, setSession] = useState<any>(null);

  const profileData = useAppSelector((state) => state.userReducer);
  useEffect(() => {
    const fetchToken = async () => {
      const primaryPolicyHolderId = getPrimaryPolicyHolderId(
        policyHolders,
        profileData.email[0]?.email ?? "",
      );

      const isIntrospectionValid = await introspectionAPI();
      if (isIntrospectionValid?.response) {
        setIsLoading(true);
        const response = await createPaymentAccessToken(
          policyNumber,
          primaryPolicyHolderId,
          PAYMENT_KEYS.sourceName,
          "web",
        );

        if (response) {
          setSession(toPaymentSession(response));
        } else {
          onError(response.errorCode);
        }
      } else {
        onError(messages.error.sessionExpired);
      }

      setIsLoading(false);
    };
    fetchToken();
  }, []);

  useEffect(() => {
    if (session) {
      generateModal();
    }
  }, [session]);

  function subscribeToEvents(container: HTMLElement) {
    if (!container) {
      return;
    }
    container.addEventListener(PAYMENT_KEYS.onload, function (ev: any) {
      console.log("PortalOne.load event received", ev?.detail || ev);
      // keep loading state; actual completion handled by paymentComplete
    });

    container.addEventListener(PAYMENT_KEYS.onUnLoad, function (ev: any) {
      console.log("PortalOne.unload event received", ev?.detail || ev);
      onClose();
    });

    container.addEventListener(PAYMENT_KEYS.onError, function (ev: any) {
      console.log("PortalOne.error event received", ev?.detail || ev);
      // TO-DO: handle errors appropriately (if required in future)
    });

    container.addEventListener(
      PAYMENT_KEYS.onPaymentComplete,
      function (ev: any) {
        try {
          const payload = ev?.detail || ev || {};
          console.log("PortalOne.paymentComplete event received", payload);
        } catch (err) {
          console.log("Error handling paymentComplete event", err);
        }
      },
    );

    container.addEventListener(
      PAYMENT_KEYS.onPaymentCanceled,
      function (ev: any) {
        try {
          const payload = ev?.detail || ev || {};
          console.log("PortalOne.paymentCanceled event received", payload);
          setIsLoading(false);
          onClose();
        } catch (err) {
          console.log("Error handling paymentCanceled event", err);
        }
      },
    );
  }

  const generateModal = async () => {
    try {
      const portalOneDialog = containerRef.current;
      const PortalOne = window?.OneInc?.PortalOne;

      if (!portalOneDialog || !PortalOne) {
        return;
      }

      const portalOneInstance = new PortalOne(
        containerRef.current,
        subscribeToEvents,
      );

      const payableAmount = getPaymentInfo(
        session.noticeAmountDue,
        session.accountBalance,
      );

      if (Number(payableAmount) <= 0) {
        onError("Payment needs to be greater than 0.");
        return;
      }
      if (session) {
        portalOneInstance.makePayment(
          toOneIncPaymentSession(session, Number(payableAmount)),
        );
      }

      closePayeeModal();
    } catch (error) {}
  };

  return { isLoading };
};

export default usePayment;
